'use client';
import React from 'react';
import {
  AutocompleteModule,
  ActionModule,
  SearchModule,
  TagModule,
  ClipboardModule,
  QueryScore,
  isNoCommaString,
} from './Modules';
import { SearchContext, SearchContextType } from '../context';
import { dropdownDescriptionHelper } from '../SearchBar';
import { ChatContext, ChatContextValue } from 'stream-chat-react';
import {
  AuthorItemSpecifier,
  AuthorSearchItem,
  AuthorSearchItemData,
  AuthorSearchItemVersion,
  AuthorSearchModuleId,
  SearchItem,
  createAuthorSearchItem,
  isAuthorSearchItem,
  isLegacyAuthorSearchItem,
} from '@benzinga/search-modules';
import { cancelableDebounce } from '@benzinga/utils';

const AUTHOR_SEARCH_TIMER = 500;

export interface AuthorSearchModule extends SearchModule<AuthorSearchItemData> {
  id: typeof AuthorSearchModuleId;
  autocomplete: AutocompleteModule<AuthorSearchItemData>;
  tag: TagModule;
  action: ActionModule;
  clipboard: ClipboardModule;
}
const [debouncedQueryUsers, cancelDebouncedQueryUsers] = cancelableDebounce(
  (
    chatContext: ChatContextValue,
    text: string,
    limit: number,
    moduleSelectedItems: AuthorSearchItem[],
    resolve: (value: any) => void,
    canceled: () => boolean,
  ) => {
    chatContext?.client?.queryUsers({ name: { $autocomplete: text } }, [], { limit }).then(result => {
      if (canceled()) {
        return resolve({ canceled: true });
      }
      const users = result.users as { id: string; name?: string }[];
      resolve({
        canceled: false,
        ok: users
          .filter(item => !moduleSelectedItems.some(selectedItem => selectedItem.data.authorId === item.id))
          .slice(0, limit)
          .map<[SearchItem<AuthorSearchItemData>, QueryScore]>(item => [
            createAuthorSearchItem(item.id, item.name ?? 'chat user not found'),
            item.name?.toLowerCase() === text.toLowerCase()
              ? QueryScore.FullMatch
              : item.name?.toLowerCase().startsWith(text.toLowerCase())
                ? QueryScore.StartsWithMatch
                : QueryScore.IncludesMatch,
          ]),
      });
    });
  },
  AUTHOR_SEARCH_TIMER,
);
export const createAuthorAutocompleteModule = (
  searchContext: SearchContextType,
  chatContext?: ChatContextValue,
  defaultAuthors?: AuthorSearchItem[],
): AutocompleteModule<AuthorSearchItemData> => {
  return {
    dropdown: (item: SearchItem) => {
      if (!isAuthorSearchItem(item)) return undefined;
      return {
        component: dropdownDescriptionHelper(AuthorSearchModuleId, item.data.authorName),
        id: item.id,
        locked: false,
        searchItem: item,
        type: 'AutocompleteDropdown',
        version: AuthorSearchItemVersion,
      };
    },
    onSelect: (item: SearchItem) => {
      if (!isAuthorSearchItem(item)) return undefined;
      return item;
    },
    query: (text: string, limit: number, selectedItems?: SearchItem[]) => {
      let canceled = false;
      const moduleSelectedItems = selectedItems?.flatMap(item => (isAuthorSearchItem(item) ? [item] : [])) ?? [];
      return {
        cancel: () => {
          canceled = true;
          cancelDebouncedQueryUsers();
        },
        promise: new Promise(resolve => {
          if (text === '') {
            return resolve({
              canceled: false,
              ok: [
                ...searchContext.getRecentModuleItem(undefined).filter(isAuthorSearchItem),
                ...(defaultAuthors ?? []),
              ]
                .filter(
                  item => !moduleSelectedItems.some(selectedItem => selectedItem.data.authorId === item.data.authorId),
                )
                .filter(
                  (item, idx, arr) =>
                    !arr.some(
                      (otherItem, otherIdx) => otherItem.data.authorId === item.data.authorId && otherIdx < idx,
                    ),
                )
                .slice(0, limit)
                .map<[SearchItem<AuthorSearchItemData>, QueryScore]>(item => [item, QueryScore.HistoryMatch]),
            });
          }

          if (!chatContext) {
            return resolve({
              canceled: false,
              err: new Error('Chat Context not provided'),
            });
          }

          debouncedQueryUsers(chatContext, text, limit, moduleSelectedItems, resolve, () => canceled);
        }),
      };
    },
    specifier: AuthorItemSpecifier,
  };
};

export const createAuthorTagModule = (): TagModule => {
  return {
    render: item => {
      if (!isAuthorSearchItem(item)) return undefined;
      return {
        text: item.data.authorName,
        type: 'text',
      };
    },
    tagInputText: item => {
      if (!isAuthorSearchItem(item)) return undefined;
      return item.data.authorName;
    },
  };
};

export const createAuthorActionModule = (): ActionModule => {
  return {
    dropdown: () => {
      return [];
    },
  };
};

export const createAuthorClipboardModule = (): ClipboardModule => {
  return {
    onCopyJSON: token => {
      if (!isAuthorSearchItem(token)) return undefined;
      return [token];
    },
    onCopyString: token => {
      if (!isAuthorSearchItem(token)) return undefined;
      const str = `${AuthorItemSpecifier}${token.data.authorId}:${token.data.authorName}`;
      return isNoCommaString(str) ? [str] : undefined;
    },
    onPasteJSON: (token): AuthorSearchItem | undefined => {
      if (isLegacyAuthorSearchItem(token)) return AuthorModuleItemLoader(token);
      return undefined;
    },
    onPasteString: str => {
      if (str.startsWith(AuthorItemSpecifier)) {
        const authorData = str.slice(AuthorItemSpecifier.length).split(':');
        if (authorData.length !== 2) return undefined;
        return createAuthorSearchItem(authorData[0], authorData[1]);
      }
      return undefined;
    },
  };
};

const AuthorModuleItemLoader = (item: SearchItem) => {
  if (!isLegacyAuthorSearchItem(item)) return undefined;

  switch (item.version) {
    case AuthorSearchItemVersion:
    default:
      return item;
  }
};

export const createAuthorSearchModule = (
  searchContext: SearchContextType,
  chatContext?: ChatContextValue,
  defaultAuthors?: AuthorSearchItem[],
): AuthorSearchModule => {
  return {
    action: createAuthorActionModule(),
    autocomplete: createAuthorAutocompleteModule(searchContext, chatContext, defaultAuthors),
    clipboard: createAuthorClipboardModule(),
    id: AuthorSearchModuleId,
    loader: AuthorModuleItemLoader,
    tag: createAuthorTagModule(),
  };
};

export const useAuthorSearchModule = (defaultAuthors?: AuthorSearchItem[]) => {
  const searchContext = React.useContext(SearchContext);
  const chatContext = React.useContext(ChatContext);
  return React.useMemo(
    () => createAuthorSearchModule(searchContext, chatContext, defaultAuthors),
    [chatContext, searchContext, defaultAuthors],
  );
};
